import { Camera, Upload, Video, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'

/**
 * Capturing a specimen: a file the operator already has, or a photograph taken there and then.
 *
 * Both paths end the same way — a `Blob` handed to the caller's own upload mutation — because the
 * server does not care how the bytes arrived, only that they satisfy `ACCEPTED_TYPES` and
 * `MAX_SIZE_BYTES`. Both are checked here first: a refusal a teller reads instantly, keyed to what
 * they just picked, is a better answer than a spinner that ends in a problem-details toast for a
 * 9MB photo from a modern phone camera.
 */

/** Mirrors `ObjectStoreContentTypes.Accepted` on the server — jpeg and png only. */
const ACCEPTED_MEDIA_TYPES = ['image/jpeg', 'image/png'] as const

/** Mirrors `ObjectStorage.MaxSizeBytes` in the server's configuration — 2 MiB. */
const MAX_MEDIA_SIZE_BYTES = 2 * 1024 * 1024

function describeMediaRejection(file: File): string | null {
  if (!ACCEPTED_MEDIA_TYPES.includes(file.type as (typeof ACCEPTED_MEDIA_TYPES)[number])) {
    return `${file.type || 'That file type'} is not accepted. Use a JPEG or PNG image.`
  }

  if (file.size > MAX_MEDIA_SIZE_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1)
    return `That file is ${mb} MB. The limit is 2 MB.`
  }

  return null
}

/**
 * A file picker that validates before it ever calls `onSelect`, so a rejected file never reaches
 * the network — the server would refuse it identically with `object_store.content_type_not_accepted`
 * or `object_store.too_large`, but only after a round trip this catches instantly instead.
 */
export function MediaUploadButton({
  label,
  onSelect,
  disabled,
  isPending,
}: {
  label: string
  onSelect: (file: File) => void
  disabled?: boolean
  isPending?: boolean
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [rejection, setRejection] = useState<string | null>(null)

  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MEDIA_TYPES.join(',')}
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (!file) {
            return
          }

          const problem = describeMediaRejection(file)
          setRejection(problem)
          if (!problem) {
            onSelect(file)
          }
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled || isPending}
        onClick={() => inputRef.current?.click()}
      >
        {isPending ? <Spinner /> : <Upload />}
        {label}
      </Button>
      {rejection ? <p className="text-2xs text-danger-text">{rejection}</p> : null}
    </div>
  )
}

/**
 * Taking a photograph through the device's own camera, for the passport photo specifically — a
 * signature or a thumbprint is a physical mark on paper that a camera photographing the passbook
 * would capture badly; those stay file-upload only, of a scan taken elsewhere.
 *
 * `getUserMedia` is asked for only once the operator presses the button, never on mount — a customer
 * detail screen loading in the background must not light up the camera indicator uninvited. Every
 * failure — no camera, permission refused, an insecure context — folds into the same fallback
 * message and leaves the ordinary file upload button standing right beside it.
 */
export function WebcamCapture({
  onCapture,
  disabled,
  isPending,
}: {
  onCapture: (blob: Blob) => void
  disabled?: boolean
  isPending?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = stream
    }

    return () => {
      stream?.getTracks().forEach((track) => track.stop())
    }
  }, [stream])

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser cannot reach a camera. Use Upload a file instead.')
      return
    }

    setStarting(true)
    setError(null)

    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      })
      setStream(media)
    } catch {
      // Every failure reads the same to an operator standing at a counter: the camera is not an
      // option right now, whatever the reason — permission refused, no device, or the page is not
      // served over HTTPS, which the API this needs is unavailable under at all.
      setError('The camera could not be started. Use Upload a file instead.')
    } finally {
      setStarting(false)
    }
  }

  const stop = () => {
    stream?.getTracks().forEach((track) => track.stop())
    setStream(null)
  }

  const capture = () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d')
    if (!context) {
      return
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCapture(blob)
        }
        stop()
      },
      'image/jpeg',
      0.9,
    )
  }

  if (stream) {
    return (
      <div className="space-y-2 rounded-card border border-border-subtle bg-surface p-2">
        <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-control bg-black" />
        <div className="flex gap-2">
          <Button type="button" variant="primary" size="sm" disabled={isPending} onClick={capture}>
            {isPending ? <Spinner /> : <Camera />}
            Capture
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={stop}>
            <X />
            Cancel
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled || starting}
        onClick={() => void start()}
      >
        {starting ? <Spinner /> : <Video />}
        Use the camera
      </Button>
      {error ? <p className="text-2xs text-danger-text">{error}</p> : null}
    </div>
  )
}
